import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import Grid from "core/Grid";

import PhotosSectionCard from "./PhotosSectionCard";
import {
    PhotosPricingTableHeaderBasic,
    PhotosPricingTableHeaderLarge,
    PhotosPricingTableHeaderFullRes
} from "./photosPricingTableHeaders";

import Close from "core/ReusableIcons/Close";
import Checkmark from "core/ReusableIcons/Checkmark";

import colors from "core/colors";
import {
    Table,
    TableRow,
    TableColumnHeading,
    TableSubheading,
    TableData,
    TableBody,
    TableRowHeading,
    TableHead
} from "core/Table";
import { StyledLink } from "./styles";

import { photosPricingTableHeadingIds as headingIds } from "./data";

let PhotosPricingTableDesktop = ({ className }) => {
    const falseIcon = (
        <Close fillColor={colors.accent.darkMode.suenosBuenos} size="md" />
    );
    const trueIcon = (
        <Checkmark fillColor={colors.accent.lightMode.persian} size="md" />
    );

    return (
        <>
            <Table className={className}>
                <TableHead>
                    <TableRow>
                        <TableData empty />
                        <TableColumnHeading id={headingIds.basic} scope="col">
                            <PhotosPricingTableHeaderBasic />
                        </TableColumnHeading>
                        <TableColumnHeading id={headingIds.large} scope="col">
                            <PhotosPricingTableHeaderLarge />
                        </TableColumnHeading>
                        <TableColumnHeading id={headingIds.fullRes} scope="col">
                            <PhotosPricingTableHeaderFullRes />
                        </TableColumnHeading>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableSubheading
                            id={headingIds.licenseAndQuality}
                            colspan="4"
                            scope="colgroup"
                        >
                            License &amp; Quality
                        </TableSubheading>
                    </TableRow>
                    <TableRow>
                        <TableRowHeading
                            headers={headingIds.licenseAndQuality}
                            id={headingIds.resolution}
                            align="left"
                        >
                            Resolution
                        </TableRowHeading>
                        <TableData
                            headers={`${headingIds.basic} ${headingIds.licenseAndQuality} ${headingIds.resolution}`}
                        >
                            .2 MP
                        </TableData>
                        <TableData
                            headers={`${headingIds.large} ${headingIds.licenseAndQuality} ${headingIds.resolution}`}
                        >
                            5 MP
                        </TableData>
                        <TableData
                            headers={`${headingIds.fullRes} ${headingIds.licenseAndQuality} ${headingIds.resolution}`}
                        >
                            10+ MP
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableRowHeading
                            headers={headingIds.licenseAndQuality}
                            id={headingIds.license}
                            align="left"
                        >
                            License
                        </TableRowHeading>
                        <TableData
                            headers={`${headingIds.basic} ${headingIds.licenseAndQuality} ${headingIds.license}`}
                            fontSize="0.75rem"
                        >
                            <StyledLink
                                href="/legal/terms-of-use/#photo-licenses"
                                target="_blank"
                            >
                                Creative Commons
                                <br />
                                (CC BY-NC-ND 2.0)
                            </StyledLink>
                        </TableData>
                        <TableData
                            headers={`${headingIds.large} ${headingIds.licenseAndQuality} ${headingIds.license}`}
                            fontSize="0.75rem"
                        >
                            <StyledLink
                                href="/legal/terms-of-use/#photo-licenses"
                                target="_blank"
                            >
                                Royalty-Free
                                <br />
                                Noncommercial License
                            </StyledLink>
                        </TableData>
                        <TableData
                            headers={`${headingIds.fullRes} ${headingIds.licenseAndQuality} ${headingIds.license}`}
                            fontSize="0.75rem"
                        >
                            <StyledLink
                                href="/legal/terms-of-use/#photo-licenses"
                                target="_blank"
                            >
                                Royalty-Free Limited
                                <br />
                                Commercial License
                            </StyledLink>
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableSubheading
                            id={headingIds.usageSummary}
                            colspan="4"
                            scope="colgroup"
                        >
                            Usage Summary*
                        </TableSubheading>
                    </TableRow>
                    <TableRow>
                        <TableRowHeading
                            headers={headingIds.usageSummary}
                            id={headingIds.worldwide}
                            align="left"
                        >
                            Worldwide, in-perpetuity
                        </TableRowHeading>
                        <TableData
                            headers={`${headingIds.basic} ${headingIds.usageSummary} ${headingIds.worldwide}`}
                        >
                            {trueIcon}
                        </TableData>
                        <TableData
                            headers={`${headingIds.large} ${headingIds.usageSummary} ${headingIds.worldwide}`}
                        >
                            {trueIcon}
                        </TableData>
                        <TableData
                            headers={`${headingIds.fullRes} ${headingIds.usageSummary} ${headingIds.worldwide}`}
                        >
                            {trueIcon}
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableRowHeading
                            headers={headingIds.usageSummary}
                            id={headingIds.printedMaterials}
                            align="left"
                        >
                            Printed materials
                        </TableRowHeading>
                        <TableData
                            headers={`${headingIds.basic} ${headingIds.usageSummary} ${headingIds.printedMaterials}`}
                        >
                            One project
                        </TableData>
                        <TableData
                            headers={`${headingIds.large} ${headingIds.usageSummary} ${headingIds.printedMaterials}`}
                        >
                            Up to 100 copies
                        </TableData>
                        <TableData
                            headers={`${headingIds.fullRes} ${headingIds.usageSummary} ${headingIds.printedMaterials}`}
                        >
                            Up to 250,000 copies
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableRowHeading
                            headers={headingIds.usageSummary}
                            id={headingIds.cropImage}
                            align="left"
                        >
                            Crop image
                        </TableRowHeading>
                        <TableData
                            headers={`${headingIds.basic} ${headingIds.usageSummary} ${headingIds.cropImage}`}
                        >
                            {falseIcon}
                        </TableData>
                        <TableData
                            headers={`${headingIds.large} ${headingIds.usageSummary} ${headingIds.cropImage}`}
                        >
                            {trueIcon}
                        </TableData>
                        <TableData
                            headers={`${headingIds.fullRes} ${headingIds.usageSummary} ${headingIds.cropImage}`}
                        >
                            {trueIcon}
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableRowHeading
                            headers={headingIds.usageSummary}
                            id={headingIds.withoutAttribution}
                            align="left"
                        >
                            Use without attribution
                        </TableRowHeading>
                        <TableData
                            headers={`${headingIds.basic} ${headingIds.usageSummary} ${headingIds.withoutAttribution}`}
                        >
                            {falseIcon}
                        </TableData>
                        <TableData
                            headers={`${headingIds.large} ${headingIds.usageSummary} ${headingIds.withoutAttribution}`}
                        >
                            {trueIcon}
                        </TableData>
                        <TableData
                            headers={`${headingIds.fullRes} ${headingIds.usageSummary} ${headingIds.withoutAttribution}`}
                        >
                            {trueIcon}
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableRowHeading
                            headers={headingIds.usageSummary}
                            id={headingIds.edit}
                            align="left"
                        >
                            Edit, build upon, make derivative
                        </TableRowHeading>
                        <TableData
                            headers={`${headingIds.basic} ${headingIds.usageSummary} ${headingIds.edit}`}
                        >
                            {falseIcon}
                        </TableData>
                        <TableData
                            headers={`${headingIds.large} ${headingIds.usageSummary} ${headingIds.edit}`}
                        >
                            {falseIcon}
                        </TableData>
                        <TableData
                            headers={`${headingIds.fullRes} ${headingIds.usageSummary} ${headingIds.edit}`}
                        >
                            {trueIcon}
                        </TableData>
                    </TableRow>
                    <TableRow>
                        <TableRowHeading
                            headers={headingIds.usageSummary}
                            id={headingIds.limitedCommercialUse}
                            align="left"
                        >
                            Limited commercial use
                        </TableRowHeading>
                        <TableData
                            headers={`${headingIds.basic} ${headingIds.usageSummary} ${headingIds.limitedCommercialUse}`}
                        >
                            {falseIcon}
                        </TableData>
                        <TableData
                            headers={`${headingIds.large} ${headingIds.usageSummary} ${headingIds.limitedCommercialUse}`}
                        >
                            {falseIcon}
                        </TableData>
                        <TableData
                            headers={`${headingIds.fullRes} ${headingIds.usageSummary} ${headingIds.limitedCommercialUse}`}
                        >
                            {trueIcon}
                        </TableData>
                    </TableRow>
                </TableBody>
            </Table>
            <Grid tabletSpacing={20} desktopSpacing={20}>
                <Grid item sm={12} md={6} lg={6}>
                    <PhotosSectionCard />
                </Grid>
                <Grid item sm={12} md={6} lg={6}>
                    <PhotosSectionCard editorialUseOnly />
                </Grid>
            </Grid>
        </>
    );
};

PhotosPricingTableDesktop = styled(PhotosPricingTableDesktop)``;
PhotosPricingTableDesktop.propTypes = {
    className: PropTypes.string
};

export default PhotosPricingTableDesktop;
